// Keeping the query cache across reloads (cloud): what the screen showed last is
// drawn at once from the copy, and fetched again behind it.
//
// Promise:
// - `restorePersistedCache` puts the copy back before the first render (waiting
//   at most RESTORE_TIMEOUT_MS so a stuck store never blocks the start), marks
//   everything stale so the screens fetch again, and keeps writing changes.
// - Only queries the parts register (`persist`) are kept, and only successful
//   ones. Long lists keep the pages that were shown, up to PERSISTED_MAX_PAGES.
// - A copy older than a day, or from another build (`buster`), is dropped.
// - `clearPersistedCache` removes the copy (sign out: the next person must not
//   see the titles of the previous one).
// The offline edition uses it only while it shows another PC's data (its own
// server is on the same PC).
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
  type Persister,
  persistQueryClientRestore,
  persistQueryClientSubscribe,
} from "@tanstack/query-persist-client-core";
import type { QueryClient, QueryKey } from "@tanstack/react-query";

export type QueryPersister = Persister;

export const PERSISTED_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const PERSISTED_MAX_PAGES = 5;
const RESTORE_TIMEOUT_MS = 1000;

/** Infinite results keep the pages that were shown, the newest ones first. */
export function trimForPersist(data: unknown): unknown {
  const d = data as { pages?: unknown; pageParams?: unknown } | null;
  if (
    d &&
    typeof d === "object" &&
    Array.isArray(d.pages) &&
    Array.isArray(d.pageParams) &&
    d.pages.length > PERSISTED_MAX_PAGES
  )
    return {
      pages: d.pages.slice(0, PERSISTED_MAX_PAGES),
      pageParams: d.pageParams.slice(0, PERSISTED_MAX_PAGES),
    };
  return data;
}

export interface PersistOptions {
  queryClient: QueryClient;
  persister: QueryPersister;
  shouldPersist(key: QueryKey): boolean;
  /** Changes with every build: a copy from another build is dropped. */
  buster: string;
}

const optionsFor = (o: PersistOptions) => ({
  queryClient: o.queryClient,
  persister: o.persister,
  maxAge: PERSISTED_MAX_AGE_MS,
  buster: o.buster,
  dehydrateOptions: {
    shouldDehydrateQuery: (q: {
      state: { status: string };
      queryKey: QueryKey;
    }) => q.state.status === "success" && o.shouldPersist(q.queryKey),
    serializeData: trimForPersist,
  },
  hydrateOptions: {
    defaultOptions: { queries: { gcTime: PERSISTED_MAX_AGE_MS } },
  },
});

/** Restores the copy and keeps it up to date. Returns the function that stops writing. */
export async function restorePersistedCache(
  o: PersistOptions,
): Promise<() => void> {
  const options = optionsFor(o);
  try {
    await Promise.race([
      persistQueryClientRestore(options).then(() =>
        o.queryClient.invalidateQueries(),
      ),
      new Promise<void>((resolve) => setTimeout(resolve, RESTORE_TIMEOUT_MS)),
    ]);
  } catch {
    // A broken copy is overwritten by the next write; the start goes on.
  }
  return persistQueryClientSubscribe(options);
}

export async function clearPersistedCache(
  persister: QueryPersister,
): Promise<void> {
  try {
    await persister.removeClient();
  } catch {
    // Signing out goes on even when the copy cannot be removed.
  }
}

const DB_NAME = "trameria-query-cache";
const STORE = "kv";

async function withStore<T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("blocked"));
  });
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = op(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

/** The browser's IndexedDB as the place for the copy. */
export function indexedDbPersister(): QueryPersister {
  return createAsyncStoragePersister({
    storage: {
      getItem: (key: string) =>
        withStore<string | undefined>("readonly", (s) => s.get(key)),
      setItem: (key: string, value: string) =>
        withStore("readwrite", (s) => s.put(value, key)),
      removeItem: async (key: string) => {
        await withStore("readwrite", (s) => s.delete(key));
      },
    },
    key: "trameria-query",
    throttleTime: 1000,
  });
}
